import { useState, type Dispatch, type SetStateAction } from 'react';
import ChevronLeft from '@/assets/icons/ChevronLeft.svg';
import ChevronRight from '@/assets/icons/ChevronRight.svg';
import { Heading } from '@/components/Heading';
import type { NavLinkProps, NavSubsection } from '@/types';
import { FeaturedLinks } from './FeaturedLinks';
import { NavItem } from './NavItem';
import * as Styled from './MenuToggle.styled';

interface SubmenuProps {
  data: Required<NavLinkProps>;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsSubmenuOpen: Dispatch<SetStateAction<boolean>>;
  setSubmenuData: Dispatch<SetStateAction<NavLinkProps | null>>;
}

export const Submenu = ({
  data,
  setIsOpen,
  setIsSubmenuOpen,
  setSubmenuData,
}: SubmenuProps) => {
  const [isTopLevel, setIsTopLevel] = useState(true);
  const [selectedItem, setSelectedItem] = useState<NavSubsection | null>(null);

  const handleBackToMenu = () => {
    setIsOpen(true);
    setIsSubmenuOpen(false);
    setSubmenuData(null);
  };

  const handleBackToTop = () => {
    setIsTopLevel(true);
    setSelectedItem(null);
  };

  const handleShowSublinks = (item: NavSubsection) => () => {
    setIsTopLevel(false);
    setSelectedItem(item);
  };

  return (
    <Styled.LinksWrapper
      aria-label={`Submenu for ${selectedItem?.heading.textContent ?? data.textContent}`}
    >
      <Styled.SubMenuToggle
        $isBackButton={true}
        handler={isTopLevel ? handleBackToMenu : handleBackToTop}
        isButton={true}
      >
        <ChevronLeft />
        All categories
      </Styled.SubMenuToggle>

      {selectedItem ? (
        <Heading level={2} styledAs={3}>
          {selectedItem.heading.href ? (
            <Styled.HeadingLink href={selectedItem.heading.href}>{selectedItem.heading.textContent}</Styled.HeadingLink>
          ) : (
            selectedItem.heading.textContent
          )}
        </Heading>
      ) : (
        <Heading level={2} styledAs={3}>
          {data.href ? (
            <Styled.HeadingLink href={data.href}>{data.textContent}</Styled.HeadingLink>
          ) : (
            data.textContent
          )}
        </Heading>
      )}

      {isTopLevel ? (
        data.submenu.map(item =>
          item.isFeatured ? (
            <FeaturedLinks
              data={item}
              highlightColour={data.highlightColour}
              key={item.heading.href}
            />
          ) : (
            <Styled.SubMenuToggle
              isButton={true}
              key={item.heading.href}
              handler={handleShowSublinks(item)}
            >
              {item.heading.textContent}
              <ChevronRight />
            </Styled.SubMenuToggle>
          ),
        )
      ) : (
        <ul>
          {selectedItem?.links.map(link => (
            <NavItem key={link.href} link={link} />
          ))}
        </ul>
      )}
    </Styled.LinksWrapper>
  );
};
